import { h } from 'preact'
import { bem } from '@nrk/dh-utils'
import { createSizeCalc, getClippedLength } from '../../../../../../lib/mandat-svg/SvgHelper'
import { MandatTableItem, sumAntall, toEnkeltmandater } from '../../../../shared/mandater'
import { MandatsamlingSvg } from '../../../../shared/MandatsamlingSvg/MandatsamlingSvg'
import { getMandatfordelingConfig } from '../../../../shared/MandatsamlingSvg/configurations'
import { getTemplate } from '../../../../shared/MandatsamlingSvg/templates'
import { placeMandater } from '../../../../../../lib/mandat-svg'

/* styles */
import styles from './MandatfordelingTable.css'
import ieStyles from './MandatfordelingTable-IE11.css'
import { flatSingle } from '../../../../../../lib/array'
import { NummerMedEnhet } from '../../../../shared/NummerMedEnhet/NummerMedEnhet'
import { Partinavn } from '../../../../shared/Partinavn/Partinavn'
import { Message } from '../../../../../../lib/intl/Message'

const cellCn = styles.mandatfordelingTable__cell
const ieItem = ieStyles.mandatfordelingTable__item

function findLength(config, mandater) {
  const template = getTemplate(config.rows)
  const table = placeMandater(template, mandater)
  return getClippedLength(table, config)
}

function cell(idx, ...modifiers) {
  /*
  IE11 doesn't support auto-fit, auto-flow or custom properties.
  Need to generate custom class name to tell where to place item.
  Can be removed if we drop IE support */
  const ieClass = ' ' + bem(ieItem, idx.toString())

  return bem(cellCn, ...modifiers) + ieClass
}

type Props = {
  items: MandatTableItem[]
}

export function MandatfordelingTable(props: Props) {
  const { items } = props
  const largest = items.reduce(
    (acc, { antall }) => (antall !== undefined ? Math.max(antall, acc) : acc),
    0
  )
  const totaltAntall = sumAntall(items)
  const config = getMandatfordelingConfig(totaltAntall, largest)

  return (
    <div class={bem(styles.mandatfordelingTable, `${items.length}-items`)}>
      <div class={cell(0, 'parti', 'header')}>
        <Message id="mandatfordeling-label-parti" />
      </div>
      <div class={cell(0, 'antall', 'header')} />
      <div class={cell(0, 'bar', 'header')} />
      <div class={cell(0, 'endring', 'header')}>
        <Message id="mandatfordeling-label-endring" />
      </div>
      {flatSingle(
        items.map((item, idx) => {
          const index = idx + 1
          const style = { '--item-index': index }
          const mandater = toEnkeltmandater([item])
          const length = findLength(config, mandater)
          const lengthRatio = length / config.radius
          const missingOneRatio = (config.radius + 0.5 * config.spacing) / config.radius
          const missingTwoRatio = (2 * config.radius + config.spacing) / config.radius

          return [
            <div class={cell(index, 'parti')} style={style} key={`p-${idx}`}>
              <h4>
                <Partinavn kortNavn={item.kortNavn} navn={item.navn} />
              </h4>
            </div>,
            <div
              key={`a-${idx}`}
              class={cell(index, 'antall')}
              style={{
                ...style,
                '--bar-length': createSizeCalc(config.radius, '100vw', lengthRatio),
                '--max-bar-length': createSizeCalc(config.radius, '800px', lengthRatio)
              }}
            >
              <NummerMedEnhet value={item.antall} enhetId={'enhet-mandater'} />
            </div>,
            <div
              key={`b-${idx}`}
              class={cell(index, 'bar', {
                manglerEn: config.rows - (item.antall || 0) === 1,
                manglerTo: config.rows - (item.antall || 0) === 2
              })}
              style={{
                ...style,
                '--offset-mangler-en': createSizeCalc(config.radius, '100vw', missingOneRatio),
                '--offset-mangler-en-max': createSizeCalc(config.radius, '800px', missingOneRatio),
                '--offset-mangler-to': createSizeCalc(config.radius, '100vw', missingTwoRatio),
                '--offset-mangler-to-max': createSizeCalc(config.radius, '800px', missingTwoRatio)
              }}
            >
              <MandatsamlingSvg items={[item]} config={config} />
            </div>,
            <div key={`e-${idx}`} class={cell(index, 'endring')} style={style}>
              <NummerMedEnhet
                prefixId="mandatfordeling-endring"
                value={item.endringAntall}
                enhetId={'enhet-mandater'}
                options={{ signDisplay: 'exceptZero' }}
              />
            </div>
          ]
        })
      )}
    </div>
  )
}
